import { useState } from "react";
import { FaEye, FaChevronDown } from "react-icons/fa";
import { IoCashOutline } from "react-icons/io5";
import { CiCreditCard1 } from "react-icons/ci";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatMoney } from "@/utils/helpers";
import { AppConfig } from "@/utils/AppConfig";
import { CashierGroup, CashierOrderHistory } from "@/types/ApiTypes";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import CashierChildrenHistoryModal from "./CashierChildrenHistoryModal";
import toast from "react-hot-toast";
import axios from "axios";
import CashierGroupInvoice from "./CashierGroupInvoice";

type PayLaterTableRowProps = {
  group: CashierGroup;
  setRefetch: (refetch: boolean) => void;
};

const PayLaterTableRow = ({ group, setRefetch }: PayLaterTableRowProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [cashierModalOpen, setCashierModalOpen] = useState(false);
  const [cashierChildModalOpen, setCashierChildModalOpen] = useState<
    string | null
  >(null);
  const [showChildOrders, setShowChildOrders] = useState(false);
  const toggleCashierModal = () => setCashierModalOpen(!cashierModalOpen);
  const openChildCashierModal = (_id: string) => setCashierChildModalOpen(_id);
  const closeChildCashierModal = () => setCashierChildModalOpen(null);
  const toggleChildOrders = () => setShowChildOrders(!showChildOrders);
  const userData = useSelector((state: RootState) => state.user.userData);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const baseUrl = import.meta.env.VITE_BASE_URL;

  const acceptedOrders = group.orders.filter(
    (order) => order.status === "accepted",
  );

  const isGroupPaid =
    acceptedOrders.length > 0 &&
    acceptedOrders.every((order) => order?.paid === true);

  const isGroupRejectedOrCancled = group.orders?.every(
    (order) => order.status !== "accepted",
  );

  const firstAcceptedOrder =
    group.orders.find(
      (order) => order.status === "accepted" && order.progress === "delivered",
    ) ||
    group.orders.find((order) => order.status === "accepted") ||
    group.orders[0];

  async function updateStatus(order: CashierOrderHistory) {
    if (order.progress === "cancelled") {
      toast.error(t("cashierHistoryBody.cannotUpdateStatus") as string);
      return;
    }

    const toastId = toast.loading(t("cashierHistoryBody.updating") as string);
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/cashier/orders/${order._id}/update?locale=${locale}`,
        method: "PUT",
        headers: { Authorization: token },
        data: { status: "returned" },
      };
      const response = await axios.request(options);
      if (response.data.success) {
        toast.success(t("cashierHistoryBody.updateSuccess") as string);
        setRefetch(true);
      } else {
        toast.error(response.data.error);
      }
    } catch (error: unknown) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.error || t("products.unexpectedError")
        : t("products.unexpectedError");
      toast.error(message as string);
    } finally {
      toast.dismiss(toastId);
    }
  }

  async function updateOrderProgress({
    id,
    progress,
  }: {
    id: string;
    progress: string;
  }) {
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/cashier/orders/${id}/update?locale=${locale}`,
        method: "PUT",
        headers: { Authorization: token },
        data: { progress },
      };
      const response = await axios.request(options);
      if (response.data.success) {
        toast.success(
          t("ordersBody.OrderProgressUpdatedSuccessfully") as string,
        );
        setRefetch(true);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.error || t("products.unexpectedError")
        : t("products.unexpectedError");
      toast.error(message as string);
    }
  }

  async function cancelOrder({ id }: { id: string }) {
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/cashier/orders/${id}/cancel?locale=${locale}`,
        method: "POST",
        headers: { Authorization: token },
      };
      const response = await axios.request(options);
      if (response.data.success) {
        toast.success(t("ordersBody.OrderCanceledSuccessfully") as string);
        setRefetch(true);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.error || t("products.unexpectedError")
        : t("products.unexpectedError");
      toast.error(message as string);
    }
  }

  return (
    <>
      {cashierModalOpen && (
        <CashierGroupInvoice
          firstAcceptedOrder={firstAcceptedOrder}
          isGroupPaid={isGroupPaid}
          groupId={group._id}
          toggleCashierModal={toggleCashierModal}
        />
      )}
      <tr
        className={`border-b ${showChildOrders ? "border border-gray-300 bg-gray-100" : "border border-gray-200 bg-gray-100"}`}
      >
        <td className="px-6 py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <button onClick={toggleChildOrders} className="focus:outline-none">
              <FaChevronDown
                size={15}
                className={`transform text-sm transition-transform ${showChildOrders ? "rotate-180" : ""}`}
              />
            </button>
            {firstAcceptedOrder?.customer
              ? firstAcceptedOrder?.customer.name
              : t("cashierHistoryBody.guest")}
          </div>
        </td>

        <td className="flex flex-col px-6 py-4 text-center">
          <span className="select-none">{t("cashierHistoryBody.GroupId")}</span>
          <span>{group._id}</span>

          <span className="flex flex-col items-center rounded-md border border-gray-300 p-1">
            {t("cashierHistoryBody.dineIn")}

            <span className="flex flex-row gap-1">
              <span>
                {t("ordersBody.Floor")} :{" "}
                {firstAcceptedOrder?.dineIn?.floor?.number ||
                  t("cashierHistoryBody.notAssigned")}
              </span>
              ,
              <span>
                {t("ordersBody.Table")} :{" "}
                {firstAcceptedOrder?.dineIn?.table?.number ||
                  t("cashierHistoryBody.notAssigned")}
              </span>
            </span>
          </span>
        </td>
        {userData?.role === "owner" || userData?.role === "superadmin" ? (
          <td className="hidden px-6 py-4 md:table-cell">
            {locale === "ar"
              ? firstAcceptedOrder?.invoice.branch.nameAR
              : firstAcceptedOrder?.invoice.branch.nameEN}
          </td>
        ) : null}

        <td className="px-6 py-4 text-center">
          {locale === "en"
            ? formatDateTime({ timestamp: group.time }).formattedEnglish
            : formatDateTime({ timestamp: group.time }).formattedArabic}
        </td>

        <td className="px-6 py-4 text-center">
          <span className="flex flex-col items-center gap-1">
            {isGroupRejectedOrCancled ? (
              "-"
            ) : (
              <span className="flex flex-col items-start gap-1">
                {group.cashPaid && group?.cashPaid > 0 ? (
                  <span className="flex items-center gap-1">
                    {t("cashierHistoryBody.cash")}
                    <IoCashOutline className="text-lg text-green-700" />
                    <span className="text-green-700">
                      {formatMoney(group?.cashPaid)} {AppConfig.currency.icon()}
                    </span>
                  </span>
                ) : null}
                {group.networkPaid && group?.networkPaid > 0 ? (
                  <span className="flex items-center gap-1">
                    {t("cashierHistoryBody.creditCard")}
                    <CiCreditCard1 className="text-lg text-blue-700" />
                    <span className="text-blue-700">
                      {formatMoney(group?.networkPaid)}{" "}
                      {AppConfig.currency.icon()}
                    </span>
                  </span>
                ) : null}
                {(!group.cashPaid || group?.cashPaid <= 0) &&
                (!group.networkPaid || group?.networkPaid <= 0) ? (
                  <span className="underline">
                    {t("cashierHistoryBody.not_determined_yet")}
                  </span>
                ) : null}
              </span>
            )}

            {group.couponDiscount > 0 && (
              <span className="flex items-center gap-1 text-green-600">
                {t("cashierHistoryModal.usedCouponDiscount")}
                <span>
                  {formatMoney(group.couponDiscount)}{" "}
                  {AppConfig.currency.icon()}
                </span>
              </span>
            )}
          </span>
        </td>

        {/* group Progress */}
        <td className="py-4 text-center">-</td>

        <td className="py-4 text-center">
          <span
            className={`rounded-xl ${isGroupPaid ? "bg-green-600" : "bg-red-500"} px-3 py-[3px] font-semibold text-white`}
          >
            {isGroupPaid
              ? t("cashierHistoryBody.paid")
              : t("cashierHistoryBody.unpaid")}
          </span>
        </td>
        <td className={`px-6 py-4 text-center font-semibold`}>
          <span className="flex flex-col">
            <span dir="rtl">
              {t("cashierHistoryBody.currencyAmount", {
                value: formatMoney(group.grandTotal),
              })}{" "}
              {AppConfig.currency.icon()}
            </span>
            <span
              className={`${isGroupPaid ? "text-green-600" : "text-red-500"}`}
            >
              {isGroupPaid
                ? t("cashierHistoryBody.paid")
                : t("cashierHistoryBody.unpaid")}
            </span>
          </span>
        </td>
        <td className="space-y-2 py-4 text-center">
          {isGroupRejectedOrCancled ? (
            " - "
          ) : (
            <span
              onClick={toggleCashierModal}
              className="mx-auto flex w-16 cursor-pointer items-center justify-center gap-1 rounded-xl bg-blue-600 px-2 py-[2px] text-center text-[10px] text-white transition-colors duration-200 hover:bg-blue-700"
            >
              {t("cashierHistoryBody.preview")}
              <FaEye className="text-sm" />
            </span>
          )}
        </td>
      </tr>

      {/* CHILD ORDERS */}
      {showChildOrders && group.orders && group.orders.length > 0 && (
        <>
          {group.orders?.map((childOrder: CashierOrderHistory) => (
            <>
              {cashierChildModalOpen && (
                <CashierChildrenHistoryModal
                  order={childOrder}
                  closeChildCashierModal={closeChildCashierModal}
                  cashierChildModalOpen={cashierChildModalOpen}
                />
              )}
              <tr className="border-b-2 bg-sky-100 text-xs">
                {/* Name */}
                <td className="px-6 py-4 text-center">-</td>

                {/* ID */}
                <td className="flex flex-col px-6 py-4 text-center">
                  {childOrder._id}
                  <span>
                    {t("cashierHistoryBody.order")}#{" "}
                    {childOrder?.invoice?.orderId}
                  </span>
                </td>
                {userData?.role === "owner" ||
                userData?.role === "superadmin" ? (
                  <td className="hidden px-6 py-4 md:table-cell">-</td>
                ) : null}

                {/* Date */}
                <td className="px-6 py-4 text-center">
                  {locale === "en"
                    ? formatDateTime({ timestamp: childOrder.time })
                        .formattedEnglish
                    : formatDateTime({ timestamp: childOrder.time })
                        .formattedArabic}
                </td>

                {/* Payment Method */}
                <td className="px-6 py-4 text-center">-</td>

                {/* Progress */}
                <td className="py-4 text-center">
                  {childOrder.status === "accepted" &&
                  childOrder.progress !== "delivered" ? (
                    <div className="mx-auto w-fit rounded-xl border px-2 py-[2px] text-sm">
                      <select
                        defaultValue={childOrder.progress || ""}
                        onChange={(e) => {
                          if (e.target.value === "cancelled") {
                            cancelOrder({ id: childOrder._id });
                          } else {
                            const value = e.target.value;
                            updateOrderProgress({
                              id: childOrder._id,
                              progress: value,
                            });
                          }
                        }}
                      >
                        <option value="">{t("ordersBody.accepted")}</option>
                        <option value="preparing">
                          {t("ordersBody.preparing")}
                        </option>
                        <option value="finished">
                          {t("ordersBody.finished")}
                        </option>
                        <option value="delivered">
                          {t("ordersBody.delivered")}
                        </option>
                        <option
                          value="cancelled"
                          className="font-semibold text-red-400"
                        >
                          {t("ordersBody.cancelorders")}
                        </option>
                      </select>
                    </div>
                  ) : childOrder.status === "accepted" &&
                    childOrder.progress === "delivered" ? (
                    <span className="rounded-xl bg-green-600 px-3 py-[3px] font-semibold text-white">
                      {t("ordersBody.delivered")}
                    </span>
                  ) : // showing badge with the progress

                  childOrder.progress === "cancelled" ||
                    childOrder.status === "returned" ||
                    childOrder.status === "cancelled" ? (
                    <div className="mx-auto w-fit rounded-xl bg-red-500 px-3 py-[3px] font-semibold text-white">
                      {t("ordersBody.Cancelled")}
                    </div>
                  ) : null}
                </td>

                {/* Status */}
                <td className="py-4 text-center">
                  {childOrder.paid === true &&
                  childOrder.status === "accepted" ? (
                    <div className="mx-auto w-fit rounded-xl border px-2 py-[2px] text-sm">
                      <select
                        onChange={(e) => {
                          if (e.target.value === "returned")
                            updateStatus(childOrder);
                        }}
                        className="w-20 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                      >
                        <option value="paid">
                          {t("cashierHistoryBody.paid")}
                        </option>
                        <option value="returned">
                          {t("cashierHistoryBody.returned")}
                        </option>
                      </select>
                    </div>
                  ) : childOrder.status === "accepted" ? (
                    <div className="mx-auto w-fit rounded-xl border px-2 py-[2px] text-sm">
                      <select
                        onChange={(e) => {
                          if (e.target.value === "returned")
                            updateStatus(childOrder);
                        }}
                        className="w-20 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                      >
                        <option value="accepted">
                          {t("cashierHistoryBody.pending")}
                        </option>
                        <option disabled value="paid">
                          {t("cashierHistoryBody.paid")}
                        </option>
                        <option value="returned">
                          {t("cashierHistoryBody.returned")}
                        </option>
                      </select>
                    </div>
                  ) : (
                    <span className="rounded-xl bg-red-600 px-3 py-[3px] font-semibold text-white">
                      {t("cashierHistoryBody.returned")}
                    </span>
                  )}
                </td>

                {/* Total */}
                <td className="px-6 py-4 text-center">
                  <span dir="rtl">
                    {t("cashierHistoryBody.currencyAmount", {
                      value: formatMoney(childOrder.total),
                    })}{" "}
                    {AppConfig.currency.icon()}
                  </span>

                  {/* <span className={`${childOrder.paid ? "text-green-600" : "text-red-600"}`}>
                    {childOrder.paid ? t("cashierHistoryBody.paid") : t("cashierHistoryBody.unpaid")}
                    </span> */}
                </td>
                <td className="space-y-2 py-4 text-center">
                  <span
                    onClick={() => openChildCashierModal(childOrder._id)}
                    className="mx-auto flex w-16 cursor-pointer items-center justify-center gap-1 rounded-xl bg-blue-600 px-2 py-[2px] text-center text-[10px] text-white transition-colors duration-200 hover:bg-blue-700"
                  >
                    {t("cashierHistoryBody.preview")}
                    <FaEye className="text-sm" />
                  </span>
                </td>
              </tr>
            </>
          ))}
        </>
      )}
    </>
  );
};

export default PayLaterTableRow;
